import React, { useCallback, useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import { FaBalanceScale, FaExclamationTriangle } from "react-icons/fa"
import api from "../../libs/axios"
import { useAuth } from "../../provider/auth.provider"
import {
  apiError,
  FilterOption,
  materialFilters,
  money,
  num,
  qty,
  stripEmpty,
} from "./shared/material.api"
import {
  AsyncPicker,
  Cell,
  DataTable,
  ExportButton,
  FilterField,
  FilterRow,
  ListCard,
  RefreshButton,
  ScopeNote,
  StatTile,
  StatusTabs,
  TextInput,
  Th,
} from "./shared/MaterialUI"

/**
 * Material reconciliation.
 *
 * Six views over one ledger, all scoped to the caller's projects:
 *
 *   Group heads   Purchase / Issue / Consumption / Billable / In Stock, the
 *                 five heads the specification asks for, at a glance.
 *   Billable      Purchased / Installed / Billed / Sold / Discard / Lost, with
 *                 the gap between installed and billed called out.
 *   Consumption   the full item-wise reconciliation, with a variance column
 *                 comparing what the arithmetic says is left against what the
 *                 balances actually hold.
 *   Warehouse / Staff / Site   the same thing per holder.
 *
 * The variance column is the point of the whole screen. Anything other than
 * zero means a movement went unrecorded.
 */

type RecoKind = "group-heads" | "billable" | "consumption" | "warehouse" | "staff" | "site"

const TABS: Array<{ key: RecoKind; label: string; hint: string }> = [
  { key: "group-heads", label: "Group Heads", hint: "Purchase, Issue, Consumption, Billable and In Stock at a glance." },
  {
    key: "billable",
    label: "Billable Item Reco",
    hint: "Billable items only: purchased, installed, billed, sold, discarded, lost. The gap between installed and billed is material that was fitted but never invoiced.",
  },
  {
    key: "consumption",
    label: "Consumption Item Reco",
    hint: "Every item: opening, purchased, installed, sold, discarded, lost, adjusted, and the closing balance. Variance compares that arithmetic against the live stock.",
  },
  { key: "warehouse", label: "Warehouse Reco", hint: "Received, issued and adjusted, warehouse by warehouse." },
  {
    key: "staff",
    label: "Staff Reco",
    hint: "Issued, installed, sold, discarded, lost and adjusted, person by person. A loss shows here as a reducing line; no money is tracked.",
  },
  { key: "site", label: "Site Reco", hint: "Issued to site against installed at site, site by site." },
]

/** Which columns each reco shows, in order. Keeps the table generic. */
const COLUMNS: Record<Exclude<RecoKind, "group-heads">, Array<{ key: string; label: string; tone?: string }>> = {
  billable: [
    { key: "opening_quantity", label: "Opening" },
    { key: "purchased", label: "Purchased" },
    { key: "installed", label: "Installed" },
    { key: "billed_quantity", label: "Billed" },
    { key: "unbilled_quantity", label: "Unbilled", tone: "text-amber-700" },
    { key: "sold", label: "Sold" },
    { key: "discarded", label: "Discarded" },
    { key: "lost", label: "Lost" },
    { key: "closing_balance", label: "Closing" },
  ],
  consumption: [
    { key: "opening_quantity", label: "Opening" },
    { key: "purchased", label: "Purchased" },
    { key: "issued_internal", label: "Issued out" },
    { key: "returned_internal", label: "Returned in" },
    { key: "installed", label: "Installed" },
    { key: "sold", label: "Sold" },
    { key: "discarded", label: "Discarded" },
    { key: "lost", label: "Lost" },
    { key: "adjusted_in", label: "Adj +" },
    { key: "adjusted_out", label: "Adj -" },
    { key: "closing_balance", label: "Closing (ledger)" },
    { key: "stock_on_hand", label: "On hand" },
    { key: "variance", label: "Variance", tone: "font-semibold" },
  ],
  warehouse: [
    { key: "opening_quantity", label: "Opening" },
    { key: "received_purchase", label: "Received (purchase)" },
    { key: "received_returns", label: "Received (returns)" },
    { key: "issued", label: "Issued" },
    { key: "sold", label: "Sold" },
    { key: "discarded", label: "Discarded" },
    { key: "adjusted_in", label: "Adj +" },
    { key: "adjusted_out", label: "Adj -" },
    { key: "closing_balance", label: "Closing" },
  ],
  staff: [
    { key: "opening_quantity", label: "Opening" },
    { key: "purchased_directly", label: "Bought directly" },
    { key: "issued_to_staff", label: "Issued to them" },
    { key: "issued_onward", label: "Issued onward" },
    { key: "installed", label: "Installed" },
    { key: "sold", label: "Sold" },
    { key: "discarded", label: "Discarded" },
    { key: "lost", label: "Lost", tone: "text-red-700" },
    { key: "adjusted_in", label: "Adj +" },
    { key: "adjusted_out", label: "Adj -" },
    { key: "closing_balance", label: "Closing" },
  ],
  site: [
    { key: "opening_quantity", label: "Opening" },
    { key: "issued_to_site", label: "Issued to site" },
    { key: "delivered_direct", label: "Delivered direct" },
    { key: "returned_from_site", label: "Returned" },
    { key: "installed", label: "Installed" },
    { key: "sold", label: "Sold" },
    { key: "discarded", label: "Discarded" },
    { key: "adjusted_in", label: "Adj +" },
    { key: "adjusted_out", label: "Adj -" },
    { key: "closing_balance", label: "Closing (still at site)" },
  ],
}

const HOLDER_KINDS: RecoKind[] = ["warehouse", "staff", "site"]

const MaterialRecoPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const canExport = hasPermission("MaterialReco", "export")

  const [kind, setKind] = useState<RecoKind>("group-heads")
  const [project, setProject] = useState<FilterOption | null>(null)
  const [item, setItem] = useState<FilterOption | null>(null)
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const [billableOnly, setBillableOnly] = useState(false)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [groupHeads, setGroupHeads] = useState<any | null>(null)
  const [rows, setRows] = useState<any[]>([])
  const [totals, setTotals] = useState<Record<string, number>>({})

  const params = useMemo(
    () =>
      stripEmpty({
        project_id: project?.id,
        item_id: item?.id,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        billable_only: billableOnly ? "true" : undefined,
      }),
    [project, item, fromDate, toDate, billableOnly],
  )

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get(`/materials/reco/${kind}`, { params })
      if (kind === "group-heads") {
        setGroupHeads(res.data?.data || null)
        setRows([])
        setTotals({})
      } else {
        setGroupHeads(null)
        setRows(res.data?.data?.rows || [])
        setTotals(res.data?.data?.totals || {})
      }
    } catch (e: any) {
      toast.error(apiError(e, "Failed to load the reconciliation"))
      setRows([])
      setGroupHeads(null)
    } finally {
      setLoading(false)
    }
  }, [kind, params])

  useEffect(() => {
    load()
  }, [load])

  const exportReco = async () => {
    if (kind === "group-heads") {
      toast.error("Pick one of the item or holder reconciliations to export")
      return
    }
    setExporting(true)
    try {
      const res = await api.get(`/materials/reco/export/${kind}`, { params, responseType: "blob" })
      const url = URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement("a")
      link.href = url
      link.download = `material-${kind}-reco.xlsx`
      link.click()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      toast.error(apiError(e, "Failed to export"))
    } finally {
      setExporting(false)
    }
  }

  const activeTab = TABS.find((t) => t.key === kind)!
  const columns = kind === "group-heads" ? [] : COLUMNS[kind]
  const varianceRows = kind === "consumption" ? rows.filter((r) => Math.abs(num(r.variance)) > 0.0005) : []
  const colSpan = (HOLDER_KINDS.includes(kind) ? 1 : 0) + 2 + columns.length + (kind === "billable" ? 1 : 0)

  return (
    <div className="space-y-4">
      <ListCard
        icon={<FaBalanceScale className="w-5 h-5" />}
        title="Material Reconciliation"
        subtitle={activeTab.hint}
        actions={
          <>
            <RefreshButton loading={loading} onClick={load} />
            {canExport && kind !== "group-heads" ? <ExportButton onClick={exportReco} busy={exporting} /> : null}
          </>
        }
        filters={
          <>
            <FilterRow>
              <FilterField label="Project">
                <AsyncPicker
                  value={project}
                  onChange={setProject}
                  fetcher={(s) => materialFilters.projects(s)}
                  placeholder="All my projects"
                  emptyHint="You are not assigned to any project"
                />
              </FilterField>
              <FilterField label="Item">
                <AsyncPicker
                  value={item}
                  onChange={setItem}
                  fetcher={(s) => materialFilters.items(project ? String(project.id) : undefined, s)}
                  dependency={project?.id}
                  placeholder="All items"
                  emptyHint="No material activity in your projects yet"
                />
              </FilterField>
              <FilterField label="From" className="lg:w-40">
                <TextInput type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </FilterField>
              <FilterField label="To" className="lg:w-40">
                <TextInput type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </FilterField>
              <FilterField label="&nbsp;" className="lg:w-auto">
                <label className="flex items-center gap-2 text-sm text-gray-700 h-[38px]">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={billableOnly}
                    onChange={(e) => setBillableOnly(e.target.checked)}
                  />
                  Billable items only
                </label>
              </FilterField>
            </FilterRow>
            <StatusTabs label="View" tabs={TABS} value={kind} onChange={(k) => setKind(k as RecoKind)} />
          </>
        }
      >
        {kind === "group-heads" ? (
          <div className="p-4 sm:p-6">
            <GroupHeads data={groupHeads} loading={loading} />
          </div>
        ) : (
          <>
            {varianceRows.length ? (
              <div className="px-4 sm:px-6 py-3 border-b border-amber-200 bg-amber-50 flex items-start gap-2 text-sm text-amber-900">
                <FaExclamationTriangle className="mt-0.5 shrink-0" />
                <span>
                  <strong>{varianceRows.length} item(s) do not reconcile.</strong> The ledger arithmetic and the live
                  stock disagree, which means a movement was never recorded. Start with the largest variance.
                </span>
              </div>
            ) : null}

            <DataTable
              colSpan={colSpan}
              loading={loading}
              empty={rows.length === 0}
              loadingText="Loading reconciliation..."
              emptyText="No material activity for these filters"
              emptyHint={<ScopeNote />}
              emptyIcon={<FaBalanceScale />}
              head={
                <>
                  {HOLDER_KINDS.includes(kind) ? <Th>Holder</Th> : null}
                  <Th>Item</Th>
                  <Th>Unit</Th>
                  {columns.map((col) => (
                    <Th key={col.key} className="text-right">
                      {col.label}
                    </Th>
                  ))}
                  {kind === "billable" ? <Th className="text-right">Billed Value</Th> : null}
                </>
              }
              foot={
                Object.keys(totals).length ? (
                  <tfoot className="bg-gray-50 border-t border-gray-200 font-medium">
                    <tr>
                      {HOLDER_KINDS.includes(kind) ? <Cell></Cell> : null}
                      <Cell>Total</Cell>
                      <Cell></Cell>
                      {columns.map((col) => (
                        <Cell key={col.key} className="text-right">
                          {totals[col.key] === undefined ? "" : qty(totals[col.key])}
                        </Cell>
                      ))}
                      {kind === "billable" ? <Cell className="text-right">{money(totals.billed_amount)}</Cell> : null}
                    </tr>
                  </tfoot>
                ) : null
              }
            >
              {rows.map((row, index) => (
                <tr key={`${row.holder_id ?? "x"}-${row.item_id}-${index}`} className="hover:bg-gray-50 transition-colors">
                  {HOLDER_KINDS.includes(kind) ? (
                    <Cell>
                      {row.holder_label}
                      {row.holder_code ? <span className="text-xs text-gray-400"> ({row.holder_code})</span> : null}
                    </Cell>
                  ) : null}
                  <Cell>
                    <span className="font-medium">{row.item_name}</span>
                    <span className="text-xs text-gray-400"> ({row.item_code})</span>
                  </Cell>
                  <Cell className="text-gray-500">{row.unit || "-"}</Cell>
                  {columns.map((col) => {
                    const value = num(row[col.key])
                    const isVariance = col.key === "variance"
                    return (
                      <Cell
                        key={col.key}
                        className={`text-right ${col.tone || ""} ${
                          isVariance && Math.abs(value) > 0.0005 ? "text-red-700 font-semibold" : ""
                        }`}
                      >
                        {qty(value)}
                      </Cell>
                    )
                  })}
                  {kind === "billable" ? <Cell className="text-right">{money(row.billed_amount)}</Cell> : null}
                </tr>
              ))}
            </DataTable>
          </>
        )}
      </ListCard>
    </div>
  )
}

/**
 * The five group heads. In Stock is read from the balances rather than
 * recomputed from the ledger - the balances are the authority, and a mismatch
 * between the two is worth seeing rather than hiding behind a recomputation.
 */
const GroupHeads: React.FC<{ data: any; loading: boolean }> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="py-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
        <span className="text-sm text-gray-600">Loading reconciliation...</span>
      </div>
    )
  }
  if (!data) return <p className="text-sm text-gray-500">Nothing to show</p>

  const { purchase, issue, consumption, billable, in_stock } = data

  return (
    <div className="space-y-6">
      <Section
        title="Purchase"
        note="Inflow: everything that entered the company. Opening quantity is the stock loaded in from paper."
      >
        <StatTile label="Opening quantity" value={qty(purchase.opening_quantity)} />
        <StatTile label="Purchase from supplier" value={qty(purchase.purchase_from_supplier)} />
        <StatTile label="Purchase from market" value={qty(purchase.purchase_from_market)} />
        <StatTile label="Total inflow" value={qty(purchase.total_inflow)} hint={money(purchase.total_inflow_value)} />
      </Section>

      <Section
        title="Issue"
        note="Internal movement: material released from a warehouse but NOT consumed. It has only changed hands and is still on the company's books."
      >
        <StatTile label="Issued out" value={qty(issue.issued_internal)} />
        <StatTile label="Returned in" value={qty(issue.returned_internal)} />
        <StatTile
          label="Net out of warehouse"
          value={qty(issue.net_out_of_warehouse)}
          hint="Sitting with staff or at sites"
          tone="text-amber-700"
        />
      </Section>

      <Section title="Consumption" note="Outflow: everything that left the company for good.">
        <StatTile label="Consumed / installed" value={qty(consumption.consumed)} />
        <StatTile label="Sold" value={qty(consumption.sold)} />
        <StatTile label="Discarded" value={qty(consumption.discarded)} />
        <StatTile label="Lost" value={qty(consumption.lost)} tone="text-red-700" />
        <StatTile label="Adjusted in" value={qty(consumption.adjusted_in)} />
        <StatTile label="Adjusted out" value={qty(consumption.adjusted_out)} />
        <StatTile label="Total outflow" value={qty(consumption.total_outflow)} />
      </Section>

      <Section title="Billable" note="Consumed on a line marked billable, and what that is worth.">
        <StatTile label="Billable consumed" value={qty(billable.billable_consumed)} />
        <StatTile label="Billable value" value={money(billable.billable_value)} />
      </Section>

      <Section
        title="In Stock"
        note="Where the company's material physically is right now. Read from the live balances, not recomputed."
      >
        <StatTile label="In warehouse" value={qty(in_stock.warehouse)} />
        <StatTile label="With staff" value={qty(in_stock.staff)} tone="text-amber-700" />
        <StatTile label="At site" value={qty(in_stock.site)} tone="text-sky-700" />
        <StatTile
          label="Site returnable pending"
          value={qty(in_stock.site_returnable_pending)}
          hint="Issued as returnable and not yet back"
        />
        <StatTile label="Total in stock" value={qty(in_stock.total)} />
      </Section>
    </div>
  )
}

const Section: React.FC<{ title: string; note: string; children: React.ReactNode }> = ({ title, note, children }) => (
  <div>
    <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
    <p className="text-xs text-gray-500 mb-2 max-w-3xl">{note}</p>
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-3">{children}</div>
  </div>
)

export default MaterialRecoPage
